/**
 * ClientProfile.jsx — V4.2
 * Single client workspace — history views only.
 * Tabs: Overview · Purchases History · Sales History · Bank Statements
 *
 * FIXES (V4.2):
 *  - handleDeleteReport: removed premature return, replaced undefined deleteReport()
 *    with correct deleteMyReport() call inline inside onConfirm
 */

import { useState, useEffect, useRef } from "react";
import { getMyClients, getClientReports, downloadSavedExcel, deleteMyReport, replaceReportExcel, getClientBankStatements, downloadBankStatementExcel, deleteMyBankStatement } from "./api";
import ConfirmModal from "./ConfirmModal";
import ConnectedAccounts from "./ConnectedAccounts";

const CP_CSS = `
  .cp-wrap { display:flex; flex-direction:column; min-height:calc(100vh - 56px); }

  .cp-header { background:var(--surface); border-bottom:1px solid var(--border); padding:20px 24px 0; }
  .cp-back { display:inline-flex; align-items:center; gap:6px; padding:6px 12px; background:rgba(255,255,255,0.04); border:1px solid var(--border); border-radius:8px; color:var(--muted); font-size:12px; font-weight:600; cursor:pointer; transition:all 0.2s; margin-bottom:18px; }
  .cp-back:hover { background:rgba(255,255,255,0.08); color:var(--text); border-color:var(--border-blue); }

  .cp-identity { display:flex; align-items:flex-start; gap:18px; margin-bottom:20px; }
  .cp-big-avatar { width:58px; height:58px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:20px; font-weight:800; flex-shrink:0; background:linear-gradient(135deg,rgba(26,111,212,0.45),rgba(59,158,255,0.2)); color:var(--ai-blue); border:1px solid rgba(59,158,255,0.2); }
  .cp-name-block { flex:1; min-width:0; }
  .cp-client-name { font-size:22px; font-weight:800; letter-spacing:-0.02em; color:var(--text); margin-bottom:6px; }
  .cp-badges { display:flex; flex-wrap:wrap; gap:8px; }
  .cp-badge { display:inline-flex; align-items:center; gap:5px; padding:4px 11px; border-radius:20px; font-size:11px; font-weight:600; }
  .cp-badge-tin  { background:rgba(59,158,255,0.1); border:1px solid rgba(59,158,255,0.2); color:var(--ai-blue); }
  .cp-badge-vat  { background:rgba(34,211,168,0.1); border:1px solid rgba(34,211,168,0.2); color:var(--success); }
  .cp-badge-ind  { background:rgba(124,58,237,0.1); border:1px solid rgba(124,58,237,0.2); color:var(--purple); }
  .cp-badge-dot  { width:5px; height:5px; border-radius:50%; background:currentColor; }

  .cp-tabs { display:flex; gap:0; border-top:1px solid var(--border); margin-top:4px; overflow-x:auto; }
  .cp-tab { display:flex; align-items:center; gap:7px; padding:13px 16px; border:none; background:transparent; color:var(--muted); font-family:'Outfit',sans-serif; font-size:12.5px; font-weight:600; cursor:pointer; border-bottom:2px solid transparent; transition:all 0.2s; white-space:nowrap; margin-bottom:-1px; flex-shrink:0; }
  .cp-tab:hover { color:var(--muted-light); }
  .cp-tab.active { color:var(--ai-bright); border-bottom-color:var(--ai-blue); }
  .cp-tab-count { background:rgba(59,158,255,0.15); color:var(--ai-blue); padding:1px 7px; border-radius:20px; font-size:10px; font-weight:700; }

  .cp-content { flex:1; }
  .cp-overview { padding:24px; }
  .cp-ov-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:20px; }
  @media(max-width:700px){ .cp-ov-grid{grid-template-columns:1fr} }

  .cp-info-card { background:var(--panel); border:1px solid var(--border); border-radius:16px; padding:20px; }
  .cp-info-card-title { font-size:11px; font-weight:600; color:var(--muted); text-transform:uppercase; letter-spacing:0.1em; margin-bottom:14px; }
  .cp-info-row { display:flex; align-items:center; gap:10px; padding:8px 0; border-bottom:1px solid rgba(255,255,255,0.04); }
  .cp-info-row:last-child { border-bottom:none; }
  .cp-info-icon { font-size:13px; width:20px; text-align:center; flex-shrink:0; opacity:0.5; }
  .cp-info-key { font-size:12px; color:var(--muted); min-width:80px; flex-shrink:0; }
  .cp-info-val { font-size:12px; color:var(--text); flex:1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .cp-info-val.mono { font-family:'JetBrains Mono',monospace; color:var(--ai-blue); }

  .cp-return-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:20px; }
  .cp-return-stat { background:var(--panel); border:1px solid var(--border); border-radius:14px; padding:16px 18px; text-align:center; }
  .cp-return-num { font-family:'JetBrains Mono',monospace; font-size:28px; font-weight:700; line-height:1; margin-bottom:6px; }
  .cp-return-label { font-size:10px; color:var(--muted); text-transform:uppercase; letter-spacing:0.08em; }

  .cp-hist-page { padding:24px; }
  .cp-hist-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; flex-wrap:wrap; gap:12px; }
  .cp-hist-title { font-size:16px; font-weight:700; }
  .cp-hist-sub { font-size:12px; color:var(--muted); margin-top:2px; }

  .cp-hist-item { display:flex; gap:14px; align-items:center; background:var(--panel); border:1px solid var(--border); border-radius:14px; padding:14px 18px; margin-bottom:10px; transition:border-color 0.2s; }
  .cp-hist-item:hover { border-color:var(--border-blue); }
  .cp-hist-badge { width:40px; height:40px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
  .cp-hist-badge-vat  { background:rgba(59,158,255,0.1);  border:1px solid rgba(59,158,255,0.15); }
  .cp-hist-badge-sale { background:rgba(245,158,11,0.1);  border:1px solid rgba(245,158,11,0.15); }
  .cp-hist-info { flex:1; min-width:0; }
  .cp-hist-type { font-size:13px; font-weight:700; color:var(--text); margin-bottom:2px; }
  .cp-hist-meta { font-size:11px; color:var(--muted); }
  .cp-hist-actions { flex-shrink:0; display:flex; gap:8px; }

  .cp-empty { text-align:center; padding:48px 24px; }
  .cp-empty-icon { font-size:40px; opacity:0.2; margin-bottom:12px; }
  .cp-empty-title { font-size:14px; font-weight:600; color:var(--text); margin-bottom:6px; }
  .cp-empty-sub { font-size:12px; color:var(--muted); line-height:1.6; max-width:320px; margin:0 auto; }
  .cp-loading { text-align:center; padding:40px; color:var(--muted); font-size:13px; }

  .cp-spinner { display:inline-block; width:12px; height:12px; border:2px solid rgba(59,158,255,0.3); border-top-color:var(--ai-blue); border-radius:50%; animation:cp-spin 0.7s linear infinite; vertical-align:middle; margin-right:4px; }
  @keyframes cp-spin { to { transform:rotate(360deg); } }
  .cp-card-msg { font-size:11px; padding:3px 9px; border-radius:7px; margin-top:6px; display:inline-block; }
  .cp-card-msg-ok  { background:rgba(34,211,168,0.1);  color:var(--success); }
  .cp-card-msg-err { background:rgba(255,107,138,0.1); color:var(--error); }
  .btn-danger-sm { background:rgba(239,68,68,0.1); color:var(--error); border:1px solid rgba(239,68,68,0.25); border-radius:9px; padding:8px 12px; font-family:'Outfit',sans-serif; font-size:12px; font-weight:600; cursor:pointer; transition:all 0.2s; white-space:nowrap; }
  .btn-danger-sm:hover:not(:disabled) { background:rgba(239,68,68,0.18); border-color:rgba(239,68,68,0.45); }
  .btn-danger-sm:disabled { opacity:0.4; cursor:not-allowed; }
  .btn-replace-sm { background:rgba(124,58,237,0.1); color:var(--purple); border:1px solid rgba(124,58,237,0.25); border-radius:9px; padding:8px 12px; font-family:'Outfit',sans-serif; font-size:12px; font-weight:600; cursor:pointer; transition:all 0.2s; white-space:nowrap; }
  .btn-replace-sm:hover:not(:disabled) { background:rgba(124,58,237,0.18); border-color:rgba(124,58,237,0.45); }
  .btn-replace-sm:disabled { opacity:0.4; cursor:not-allowed; }
`;

const TABS = [
  { id:"overview",          icon:"⬡",  label:"Overview"            },
  { id:"purchases_history", icon:"📄", label:"Purchases History"   },
  { id:"sales_history",     icon:"🛒", label:"Sales History"       },
  { id:"bank_statements",   icon:"🏦", label:"Bank Statements"     },
];

const fmtE    = n => `E ${Number(n||0).toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "—";

function getInitials(name="") {
  return name.split(/\s+/).filter(Boolean).map(w=>w[0]?.toUpperCase()||"").slice(0,2).join("")||"?";
}

function EmptyState({ icon, title, sub }) {
  return (
    <div className="cp-empty">
      <div className="cp-empty-icon">{icon}</div>
      <div className="cp-empty-title">{title}</div>
      <div className="cp-empty-sub">{sub}</div>
    </div>
  );
}

export default function ClientProfile({ clientId, onBack }) {
  const [client,      setClient]      = useState(null);
  const [tab,         setTab]         = useState("overview");
  const [vatReports,  setVatReports]  = useState([]);
  const [saleReports, setSaleReports] = useState([]);
  const [connectorReports, setConnectorReports] = useState([]); // reports pulled via Xero/QuickBooks/Sage/Zoho — report_type ends in "_pull"
  const [bankStmts,   setBankStmts]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [downloading, setDownloading] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [busy,        setBusyMap]     = useState({});
  const [cardMsg,     setCardMsg]     = useState({});
  const replaceRef    = useRef(null);
  const replaceTarget = useRef(null);

  const setBusy  = (id, val) => setBusyMap(m => ({ ...m, [id]: val }));
  const showMsg  = (id, msg, isErr=false) => {
    setCardMsg(m => ({ ...m, [id]: { msg, isErr } }));
    setTimeout(() => setCardMsg(m => { const n={...m}; delete n[id]; return n; }), 4000);
  };

  useEffect(() => { if (clientId) load(); }, [clientId]);

  async function load() {
    setLoading(true);
    try {
      const [all, linked, bankList] = await Promise.all([
        getMyClients(),
        getClientReports(clientId).catch(() => []),
        getClientBankStatements(clientId).catch(() => []),
      ]);
      const found = all.find(c => c.id === clientId);
      if (found) setClient(found); else setError("Client not found");
      setVatReports((linked||[]).filter(r => r.report_type === "vat"));
      setSaleReports((linked||[]).filter(r => r.report_type === "sales"));
      setConnectorReports((linked||[]).filter(r => r.report_type && r.report_type.endsWith("_pull")));
      setBankStmts(bankList||[]);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const totalDocs = vatReports.length + saleReports.length + bankStmts.length + connectorReports.length;

  if (loading) return (
    <><style>{CP_CSS}</style>
      <div className="cp-loading"><div style={{fontSize:32,marginBottom:10,opacity:0.25}}>⏳</div>Loading client…</div>
    </>
  );

  if (error || !client) return (
    <><style>{CP_CSS}</style>
      <div style={{padding:32,color:"var(--error)",fontSize:14}}>✗ {error||"Client not found"}</div>
    </>
  );

  const initials = getInitials(client.name);

  /* ── FIX: Delete a purchases, sales, or connector-pulled report ── */
  async function handleDeleteReport(rpt) {
    const isConnectorPull = rpt.report_type && rpt.report_type.endsWith("_pull");
    const label = rpt.report_type === "sales" ? "Sales" : isConnectorPull ? "Connected Account" : "Purchases";
    setConfirmModal({
      title: `Delete ${label} Report?`,
      message: `This will permanently remove the report${isConnectorPull ? "" : " and its saved Excel file"} from ${client?.name || "this client"}'s history.`,
      onConfirm: async () => {
        setBusy(rpt.id, true);
        try {
          await deleteMyReport(String(rpt.report_id));
          if (rpt.report_type === "sales") setSaleReports(p => p.filter(r => r.id !== rpt.id));
          else if (isConnectorPull) setConnectorReports(p => p.filter(r => r.id !== rpt.id));
          else setVatReports(p => p.filter(r => r.id !== rpt.id));
          showMsg(rpt.id, "✓ Deleted");
        } catch(err) {
          showMsg(rpt.id, err.message, true);
        } finally {
          setBusy(rpt.id, false);
        }
      },
    });
  }

  /* ── Replace the Excel file for a purchases or sales report ── */
  function triggerReplace(rpt) {
    replaceTarget.current = rpt;
    replaceRef.current?.click();
  }

  async function handleReplaceFile(e) {
    const file = e.target.files?.[0];
    const rpt  = replaceTarget.current;
    if (!file || !rpt) return;
    e.target.value = "";
    setBusy(rpt.id, true);
    try {
      await replaceReportExcel(String(rpt.report_id), file);
      if (rpt.report_type === "sales") {
        setSaleReports(p => p.map(r => r.id === rpt.id ? { ...r, has_excel: true } : r));
      } else {
        setVatReports(p => p.map(r => r.id === rpt.id ? { ...r, has_excel: true } : r));
      }
      showMsg(rpt.id, "✓ Replaced — new file saved");
    } catch(err) {
      showMsg(rpt.id, err.message, true);
    } finally {
      setBusy(rpt.id, false);
    }
  }

  async function handleDownloadExcel(rpt) {
    if (downloading) return;
    setDownloading(rpt.id);
    try {
      const date = (rpt.created_at||"").slice(0,10) || "report";
      const label = rpt.report_type === "sales" ? "Sales" : "Purchases";
      const filename = `${label}_${client.name.replace(/\s+/g,"_")}_${date}.xlsx`;
      await downloadSavedExcel(rpt.report_id, filename);
    } catch (err) {
      alert(`Download failed: ${err.message}\n\nThe Excel file may not have been saved to the server yet. Try re-downloading from the Purchases or Sales tab.`);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <>
      <style>{CP_CSS}</style>
      <input ref={replaceRef} type="file" accept=".xlsx" style={{display:"none"}} onChange={handleReplaceFile}/>
      <div className="cp-wrap">

        {/* ── Header ── */}
        <div className="cp-header">
          <button className="cp-back" onClick={onBack}>← Back to Clients</button>

          <div className="cp-identity">
            <div className="cp-big-avatar">{initials}</div>
            <div className="cp-name-block">
              <div className="cp-client-name">{client.name}</div>
              <div className="cp-badges">
                {client.tin        && <span className="cp-badge cp-badge-tin"><span className="cp-badge-dot"/>TIN: {client.tin}</span>}
                {client.vat_number && <span className="cp-badge cp-badge-vat"><span className="cp-badge-dot"/>VAT: {client.vat_number}</span>}
                {client.industry   && <span className="cp-badge cp-badge-ind"><span className="cp-badge-dot"/>{client.industry}</span>}
              </div>
            </div>
          </div>

          <div className="cp-tabs">
            {TABS.map(t => {
              let count = 0;
              if (t.id==="purchases_history") count = vatReports.length;
              if (t.id==="sales_history")     count = saleReports.length;
              if (t.id==="bank_statements")   count = bankStmts.length;
              return (
                <button key={t.id} className={`cp-tab ${tab===t.id?"active":""}`} onClick={()=>setTab(t.id)}>
                  {t.icon} {t.label}
                  {count>0 && <span className="cp-tab-count">{count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Content ── */}
        <div className="cp-content">

          {/* ══ OVERVIEW ══ */}
          {tab==="overview" && (
            <div className="cp-overview">
              <div className="cp-return-grid" style={{gridTemplateColumns:"repeat(4,1fr)"}}>
                <div className="cp-return-stat">
                  <div className="cp-return-num" style={{color:"var(--ai-blue)"}}>{vatReports.length}</div>
                  <div className="cp-return-label">Purchases Reports</div>
                </div>
                <div className="cp-return-stat">
                  <div className="cp-return-num" style={{color:"var(--warning)"}}>{saleReports.length}</div>
                  <div className="cp-return-label">Sales Reports</div>
                </div>
                <div className="cp-return-stat">
                  <div className="cp-return-num" style={{color:"var(--teal)"}}>{bankStmts.length}</div>
                  <div className="cp-return-label">Bank Statements</div>
                </div>
                <div className="cp-return-stat">
                  <div className="cp-return-num" style={{color:"var(--muted-light)"}}>{totalDocs}</div>
                  <div className="cp-return-label">Total Documents</div>
                </div>
              </div>

              {/* ══ CONNECT ACCOUNT — pull reports from Xero / QuickBooks / Sage / Zoho ══ */}
              <div style={{marginBottom:20}}>
                <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:4}}>
                  <span style={{fontSize:17}}>🔗</span>
                  <div style={{fontSize:15,fontWeight:800,color:"var(--text)"}}>Connect Account</div>
                </div>
                <div style={{fontSize:11.5,color:"var(--muted)",marginBottom:12}}>
                  Pull reports straight from {client.name}'s Xero, QuickBooks, Sage, or Zoho Books account — no manual upload needed.
                </div>
                <ConnectedAccounts key={clientId} clientId={clientId} showHeader={false} />

                {connectorReports.length > 0 && (
                  <div style={{marginTop:16}}>
                    <div className="cp-info-card-title" style={{marginBottom:10}}>Pulled Reports History</div>
                    {connectorReports.map(rpt => (
                      <div key={rpt.id} className="cp-hist-item">
                        <div className="cp-hist-badge" style={{background:"rgba(124,58,237,0.1)",border:"1px solid rgba(124,58,237,0.15)"}}>🔗</div>
                        <div className="cp-hist-info">
                          <div className="cp-hist-type">{rpt.filename_hint || rpt.report_type}</div>
                          <div className="cp-hist-meta">{fmtDate(rpt.created_at)}</div>
                          {cardMsg[rpt.id] && <div className={`cp-card-msg ${cardMsg[rpt.id].isErr?"cp-card-msg-err":"cp-card-msg-ok"}`}>{cardMsg[rpt.id].msg}</div>}
                        </div>
                        <div className="cp-hist-actions">
                          <button
                            className="btn-danger-sm"
                            disabled={!!busy[rpt.id]}
                            title="Permanently delete this pulled report"
                            onClick={()=>handleDeleteReport(rpt)}
                          >
                            {busy[rpt.id] ? <><span className="cp-spinner"/>…</> : "🗑"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="cp-ov-grid">
                <div className="cp-info-card">
                  <div className="cp-info-card-title">Contact Details</div>
                  {client.contact_name  && <div className="cp-info-row"><span className="cp-info-icon">👤</span><span className="cp-info-key">Name</span><span className="cp-info-val">{client.contact_name}</span></div>}
                  {client.contact_email && <div className="cp-info-row"><span className="cp-info-icon">✉</span><span className="cp-info-key">Email</span><span className="cp-info-val mono">{client.contact_email}</span></div>}
                  {!client.contact_name && !client.contact_email && <div style={{fontSize:12,color:"var(--muted)",paddingTop:4}}>No contact details added.</div>}
                </div>
                <div className="cp-info-card">
                  <div className="cp-info-card-title">Registration</div>
                  {client.tin        && <div className="cp-info-row"><span className="cp-info-icon">🔑</span><span className="cp-info-key">TIN</span><span className="cp-info-val mono">{client.tin}</span></div>}
                  {client.vat_number && <div className="cp-info-row"><span className="cp-info-icon">📊</span><span className="cp-info-key">VAT No.</span><span className="cp-info-val mono">{client.vat_number}</span></div>}
                  {client.industry   && <div className="cp-info-row"><span className="cp-info-icon">🏭</span><span className="cp-info-key">Industry</span><span className="cp-info-val">{client.industry}</span></div>}
                  {client.created_at && <div className="cp-info-row"><span className="cp-info-icon">📅</span><span className="cp-info-key">Added</span><span className="cp-info-val">{fmtDate(client.created_at)}</span></div>}
                </div>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12}}>
                {[
                  {icon:"📄",label:"Purchases History", sub:`${vatReports.length} report${vatReports.length!==1?"s":""}`,       id:"purchases_history", color:"var(--ai-blue)"},
                  {icon:"🛒",label:"Sales History",     sub:`${saleReports.length} report${saleReports.length!==1?"s":""}`,     id:"sales_history",     color:"var(--warning)"},
                ].map(c=>(
                  <div key={c.id}
                    style={{background:"var(--panel)",border:"1px solid var(--border)",borderRadius:16,padding:"16px 18px",cursor:"pointer",transition:"all 0.2s"}}
                    onClick={()=>setTab(c.id)}
                    onMouseOver={e=>e.currentTarget.style.borderColor=c.color+"55"}
                    onMouseOut={e=>e.currentTarget.style.borderColor="var(--border)"}
                  >
                    <div style={{fontSize:22,marginBottom:8}}>{c.icon}</div>
                    <div style={{fontWeight:700,fontSize:13,marginBottom:3}}>{c.label}</div>
                    <div style={{fontSize:11,color:"var(--muted)"}}>{c.sub}</div>
                    <div style={{fontSize:11,color:c.color,marginTop:8,fontWeight:600}}>View →</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab==="purchases_history" && (
            <div className="cp-hist-page">
              <div className="cp-hist-header">
                <div>
                  <div className="cp-hist-title">Purchases History</div>
                  <div className="cp-hist-sub">{vatReports.length} purchases report{vatReports.length!==1?"s":""} for {client.name}</div>
                </div>
              </div>
              {vatReports.length===0
                ? <EmptyState icon="📄" title="No purchases reports yet"
                    sub="Process invoices via Purchases, select this client from the dropdown — reports appear here automatically."/>
                : vatReports.map(rpt=>(
                    <div key={rpt.id} className="cp-hist-item">
                      <div className="cp-hist-badge cp-hist-badge-vat">📄</div>
                      <div className="cp-hist-info">
                        <div className="cp-hist-type">
                          Purchases Report
                          {rpt.month && <span style={{color:"var(--muted)",fontWeight:400,marginLeft:8,fontSize:12}}>— {rpt.month}</span>}
                          {rpt.invoice_count!=null && <span style={{color:"var(--muted)",fontWeight:400,marginLeft:8,fontSize:12}}>({rpt.invoice_count} invoice{rpt.invoice_count!==1?"s":""})</span>}
                        </div>
                        <div className="cp-hist-meta">
                          <span>📅 Created: <strong style={{color:"var(--text)"}}>{fmtDate(rpt.created_at)}</strong></span>
                          <span style={{margin:"0 6px",color:"var(--border)"}}>|</span>
                          {rpt.excel_updated_at
                            ? <span>🔄 Last updated: <strong style={{color:"var(--ai-blue)"}}>{fmtDate(rpt.excel_updated_at)}</strong></span>
                            : <span>🔄 Last updated: <strong style={{color:"var(--text)"}}>{fmtDate(rpt.created_at)}</strong></span>}
                          {rpt.has_excel && <span style={{color:"var(--success)",marginLeft:10}}>✓ Excel saved</span>}
                        </div>
                        {cardMsg[rpt.id] && <div className={`cp-card-msg ${cardMsg[rpt.id].isErr?"cp-card-msg-err":"cp-card-msg-ok"}`}>{cardMsg[rpt.id].msg}</div>}
                      </div>
                      <div className="cp-hist-actions">
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={!!busy[rpt.id] || downloading===rpt.id || !rpt.has_excel}
                          title={!rpt.has_excel ? "Excel not saved yet — re-download from Purchases tab" : "Download Excel report"}
                          onClick={()=>handleDownloadExcel(rpt)}
                        >
                          {downloading===rpt.id ? <><span className="cp-spinner"/>…</> : "⬇ Download"}
                        </button>
                        <button
                          className="btn-replace-sm"
                          disabled={!!busy[rpt.id] || downloading===rpt.id}
                          title="Upload a corrected .xlsx to replace the stored file"
                          onClick={()=>triggerReplace(rpt)}
                        >
                          {busy[rpt.id] ? <><span className="cp-spinner"/>…</> : "⬆ Replace"}
                        </button>
                        <button
                          className="btn-danger-sm"
                          disabled={!!busy[rpt.id] || downloading===rpt.id}
                          title="Permanently delete this report"
                          onClick={()=>handleDeleteReport(rpt)}
                        >🗑</button>
                      </div>
                    </div>
                  ))
              }
            </div>
          )}

          {/* ══ SALES HISTORY ══ */}
          {tab==="sales_history" && (
            <div className="cp-hist-page">
              <div className="cp-hist-header">
                <div>
                  <div className="cp-hist-title">Sales History</div>
                  <div className="cp-hist-sub">{saleReports.length} sales report{saleReports.length!==1?"s":""} for {client.name}</div>
                </div>
              </div>
              {saleReports.length===0
                ? <EmptyState icon="🛒" title="No sales reports yet"
                    sub="Record sales in VAT › Sales and select this client — reports appear here automatically."/>
                : saleReports.map(rpt=>(
                    <div key={rpt.id} className="cp-hist-item">
                      <div className="cp-hist-badge cp-hist-badge-sale">🛒</div>
                      <div className="cp-hist-info">
                        <div className="cp-hist-type">
                          Sales Report
                          {rpt.month && <span style={{color:"var(--muted)",fontWeight:400,marginLeft:8,fontSize:12}}>— {rpt.month}</span>}
                        </div>
                        <div className="cp-hist-meta">
                          <span>📅 Created: <strong style={{color:"var(--text)"}}>{fmtDate(rpt.created_at)}</strong></span>
                          <span style={{margin:"0 6px",color:"var(--border)"}}>|</span>
                          {rpt.excel_updated_at
                            ? <span>🔄 Last updated: <strong style={{color:"var(--ai-blue)"}}>{fmtDate(rpt.excel_updated_at)}</strong></span>
                            : <span>🔄 Last updated: <strong style={{color:"var(--text)"}}>{fmtDate(rpt.created_at)}</strong></span>}
                          {rpt.has_excel && <span style={{color:"var(--success)",marginLeft:10}}>✓ Excel saved</span>}
                        </div>
                        {cardMsg[rpt.id] && <div className={`cp-card-msg ${cardMsg[rpt.id].isErr?"cp-card-msg-err":"cp-card-msg-ok"}`}>{cardMsg[rpt.id].msg}</div>}
                      </div>
                      <div className="cp-hist-actions">
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={!!busy[rpt.id] || downloading===rpt.id || !rpt.has_excel}
                          title={!rpt.has_excel ? "Excel not saved yet — re-download from Sales tab" : "Download sales Excel"}
                          onClick={()=>handleDownloadExcel(rpt)}
                        >
                          {downloading===rpt.id ? <><span className="cp-spinner"/>…</> : "⬇ Download"}
                        </button>
                        <button
                          className="btn-replace-sm"
                          disabled={!!busy[rpt.id] || downloading===rpt.id}
                          title="Upload a corrected .xlsx to replace the stored file"
                          onClick={()=>triggerReplace(rpt)}
                        >
                          {busy[rpt.id] ? <><span className="cp-spinner"/>…</> : "⬆ Replace"}
                        </button>
                        <button
                          className="btn-danger-sm"
                          disabled={!!busy[rpt.id] || downloading===rpt.id}
                          title="Permanently delete this report"
                          onClick={()=>handleDeleteReport(rpt)}
                        >🗑</button>
                      </div>
                    </div>
                  ))
              }
            </div>
          )}

          {/* ══ BANK STATEMENTS ══ */}
          {tab==="bank_statements" && (
            <div className="cp-hist-page">
              <div className="cp-hist-header">
                <div>
                  <div className="cp-hist-title">Bank Statements</div>
                  <div className="cp-hist-sub">{bankStmts.length} extraction{bankStmts.length!==1?"s":""} saved for {client.name}</div>
                </div>
              </div>

              {bankStmts.length === 0 ? (
                <EmptyState icon="🏦" title="No bank statements yet"
                  sub="Extract a bank statement from the Bank Statements tool and select this client to save it here." />
              ) : bankStmts.map(stmt => {
                const bankIcons = {standard_bank:"🏦", fnb:"🏛", eswatini_bank:"🏢", nedbank:"🏦"};
                const typeLabel = stmt.account_type === "loan" ? "Loan Account" : "Current Account";
                return (
                  <div key={stmt.id} className="cp-hist-item">
                    <div className="cp-hist-badge" style={{background:"rgba(14,165,233,0.1)",border:"1px solid rgba(14,165,233,0.15)"}}>
                      {bankIcons[stmt.bank] || "🏦"}
                    </div>
                    <div className="cp-hist-info">
                      <div className="cp-hist-type">
                        {stmt.bank_display || stmt.bank} · {typeLabel}
                      </div>
                      <div className="cp-hist-meta">
                        {stmt.transaction_count} transactions
                        {stmt.period ? ` · ${stmt.period}` : ""}
                        {" · "}{fmtDate(stmt.created_at)}
                        {stmt.filename_hint ? ` · ${stmt.filename_hint}` : ""}
                      </div>
                      {cardMsg[stmt.id] && (
                        <div className={`cp-card-msg ${cardMsg[stmt.id].isErr?"cp-card-msg-err":"cp-card-msg-ok"}`}>
                          {cardMsg[stmt.id].msg}
                        </div>
                      )}
                    </div>
                    <div className="cp-hist-actions">
                      {stmt.excel_exists !== false && (
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={downloading === stmt.id}
                          onClick={async () => {
                            setDownloading(stmt.id);
                            try {
                              const fn = `BankStatement_${stmt.bank_display||stmt.bank}_${fmtDate(stmt.created_at)}.xlsx`;
                              await downloadBankStatementExcel(stmt.id, fn);
                              showMsg(stmt.id, "✓ Downloaded");
                            } catch(err) {
                              showMsg(stmt.id, err.message, true);
                            } finally {
                              setDownloading(null);
                            }
                          }}>
                          {downloading === stmt.id
                            ? <><span className="cp-spinner"/>…</>
                            : "⬇ Download"}
                        </button>
                      )}
                      <button
                        className="btn-danger-sm"
                        disabled={!!busy[stmt.id]}
                        onClick={() => setConfirmModal({
                          title: "Delete Bank Statement?",
                          message: "This will permanently remove the extraction and its Excel file.",
                          onConfirm: async () => {
                            setBusy(stmt.id, true);
                            try {
                              await deleteMyBankStatement(stmt.id);
                              setBankStmts(p => p.filter(s => s.id !== stmt.id));
                              showMsg(stmt.id, "✓ Deleted");
                            } catch(err) {
                              showMsg(stmt.id, err.message, true);
                            } finally {
                              setBusy(stmt.id, false);
                            }
                          },
                        })}>
                        {busy[stmt.id] ? <><span className="cp-spinner"/>…</> : "🗑 Delete"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </div>

      <ConfirmModal state={confirmModal} onClose={() => setConfirmModal(null)} />
    </>
  );
}